/* FAQ page search and category links (sections/larcin-faq.liquid).
   Every question and answer is already in the HTML. This builds the
   category links from the categories and hides questions that don't match
   the search, so crawlers and no-JS visitors still see all of the text. */
(() => {
  const normalise = (value) =>
    value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[\u2018\u2019]/g, "'")
      .replace(/[\u201c\u201d]/g, '"')
      .replace(/\s+/g, ' ')
      .trim();

  /* When the header stays on screen, anchors and the sticky category titles
     need to stop below it. A sticky element only sticks while its parent is
     taller than it, so a header set to sticky inside a wrapper of its own
     height scrolls away and needs no room. */
  const headerOffset = () => {
    const header = document.querySelector('.lh--sticky');
    if (!header) return 0;
    const height = header.getBoundingClientRect().height;
    /* The parent that limits a sticky element is the nearest one with a box
       of its own; the header's group wrapper uses display: contents. */
    const boxParent = (el) => {
      let parent = el.parentElement;
      while (parent && getComputedStyle(parent).display === 'contents') parent = parent.parentElement;
      return parent;
    };
    for (let el = header; el && el !== document.body; el = el.parentElement) {
      const position = getComputedStyle(el).position;
      if (position === 'fixed') return Math.round(height);
      const parent = position === 'sticky' && boxParent(el);
      if (parent && parent.getBoundingClientRect().height > el.getBoundingClientRect().height + 1) {
        return Math.round(height);
      }
    }
    return 0;
  };

  const init = (root) => {
    if (!root || root.dataset.lfaqReady) return;
    root.dataset.lfaqReady = 'true';

    const input = root.querySelector('[data-lfaq-search]');
    const empty = root.querySelector('[data-lfaq-empty]');
    const status = root.querySelector('[data-lfaq-status]');
    const items = [...root.querySelectorAll('[data-lfaq-item]')];
    const groups = [...root.querySelectorAll('[data-lfaq-group]')];
    const texts = new Map(items.map((item) => [item, normalise(item.textContent)]));

    /* Category links, one per category that carries link text, in page order. */
    const nav = root.querySelector('nav[data-lfaq-nav]');
    if (nav) {
      const links = groups
        .filter((group) => group.id && group.dataset.lfaqNav)
        .map((group) => {
          const link = document.createElement('a');
          link.className = 'lfaq-category-link';
          link.href = `#${encodeURIComponent(group.id)}`;
          link.textContent = group.dataset.lfaqNav;
          link.dataset.lfaqLink = '';
          return link;
        });
      nav.replaceChildren(...links);
      nav.hidden = links.length === 0;
    }

    const setOffset = () => root.style.setProperty('--lfaq-offset', `${headerOffset()}px`);
    setOffset();

    /* A header set to show only when scrolling up slides away (.lh--away);
       the sticky category titles follow it up and back down. */
    const stickyHeader = document.querySelector('.lh--scroll-up');
    if (stickyHeader) {
      const follow = () =>
        root.style.setProperty('--lfaq-sticky', stickyHeader.classList.contains('lh--away') ? '0px' : 'var(--lfaq-offset)');
      follow();
      new MutationObserver(follow).observe(stickyHeader, { attributes: true, attributeFilter: ['class'] });
    }
    let frame = 0;
    window.addEventListener(
      'resize',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(setOffset);
      },
      { passive: true }
    );

    /* A link straight to a category (/pages/faq#shipping). On desktop the
       theme scrolls .page-wrapper, not the window, and a smooth scroll
       started before the page has loaded gets cut short; so this jumps,
       once everything has loaded and the header height is known. */
    const target = location.hash && root.querySelector(`[id="${CSS.escape(decodeURIComponent(location.hash.slice(1)))}"]`);
    if (target) {
      const jump = () => {
        setOffset();
        target.scrollIntoView({ block: 'start', behavior: 'instant' });
      };
      if (document.readyState === 'complete') requestAnimationFrame(jump);
      else window.addEventListener('load', () => requestAnimationFrame(jump), { once: true });
    }

    if (!input) return;

    /* Every word typed must appear somewhere in the question or answer. */
    const run = () => {
      const terms = normalise(input.value).split(' ').filter(Boolean);
      let shown = 0;

      for (const item of items) {
        const text = texts.get(item);
        const match = terms.every((term) => text.includes(term));
        item.hidden = !match;
        if (match) shown += 1;
      }

      for (const group of groups) {
        group.hidden = !group.querySelector('[data-lfaq-item]:not([hidden])');
      }

      if (empty) empty.hidden = !(terms.length && shown === 0);
      if (status) {
        status.textContent = terms.length ? (status.dataset.template || '[count]').replace('[count]', shown) : '';
      }
    };

    input.addEventListener('input', run);
    input.addEventListener('search', run);

    /* A category link clears an active search first, so the section it
       points to is visible before the page scrolls to it. */
    root.addEventListener('click', (event) => {
      const link = event.target.closest('[data-lfaq-link]');
      if (!link || !input.value) return;
      input.value = '';
      run();
    });

    if (input.value) run();
  };

  const start = () => document.querySelectorAll('[data-lfaq]').forEach(init);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  /* Theme editor: re-run after a section re-renders, and open a question
     when it is selected in the sidebar so its answer can be edited in view. */
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-lfaq]').forEach(init);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const item = event.target.closest && event.target.closest('details[data-lfaq-item]');
    if (item) item.open = true;
  });
})();
